import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import {
  SpecialZoomLevel,
  Viewer,
  ViewerProps,
  ViewMode,
  Worker,
} from '@react-pdf-viewer/core';
import classNames from 'classnames';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { getResolveUrl } from 'utils';
import { createZoomOptions } from './utils';
import { scrollModePlugin } from '@react-pdf-viewer/scroll-mode';
import { Dropdown, Loader } from 'ncoded-component-library';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { Size } from 'models/Size';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';

import './PdfViewer.styles.scss';
import './PdfViewer.styles.responsive.scss';

type HandleOnChangeValue =
  | number
  | SpecialZoomLevel
  | ViewMode.DualPageWithCover
  | ViewMode.DualPage;

type PdfViewerProps = {
  className?: string;
  url: string;
  isFetching?: boolean;
  canZoom?: boolean;
  loaderSize?: Size;
  isPreview?: boolean;
} & Partial<ViewerProps>;

const PdfViewer: FC<PdfViewerProps> = (props) => {
  const isPhablet = useMatchMedia('isPhablet');

  const {
    url,
    isFetching,
    className,
    loaderSize,
    canZoom = !isPhablet,
    isPreview = false,
    onDocumentLoad,
    ...rest
  } = props;

  const [numPages, setNumOfPages] = useState<number>(0);
  const zoomOptions = useRef<DropdownRef>();

  const scrollModePluginInstance = useRef(scrollModePlugin())?.current;

  const zoomPluginInstance = useRef(
    zoomPlugin({ enableShortcuts: true }),
  )?.current;

  const { switchViewMode } = scrollModePluginInstance || {};
  const { ZoomInButton, ZoomOutButton, zoomTo, CurrentScale } =
    zoomPluginInstance || {};

  const customOptions = useMemo(() => createZoomOptions(numPages), [numPages]);

  const handleOnZoom = useCallback(
    (value: HandleOnChangeValue) => {
      if (!canZoom) return;

      zoomOptions.current.setIsOpen(false);

      if (value === ViewMode.DualPage || value === ViewMode.DualPageWithCover) {
        switchViewMode(value);
        zoomTo(SpecialZoomLevel.PageFit);
        return;
      }

      switchViewMode(ViewMode.SinglePage);
      zoomTo(value);
    },
    [switchViewMode, canZoom, zoomTo],
  );

  const handleOnLoad = useCallback(
    (event) => {
      const pages = event?.doc?.numPages;
      if (pages) setNumOfPages(pages);

      onDocumentLoad?.(event);
    },
    [onDocumentLoad],
  );

  return (
    <>
      <Worker workerUrl={getResolveUrl('/pdf.worker.min.js')}>
        {isFetching ? (
          <Loader size={loaderSize} />
        ) : (
          <div
            className={classNames(
              'pdf-viewer',
              { 'pdf-viewer--preview': isPreview },
              className,
            )}
          >
            {url && (
              <Viewer
                fileUrl={url}
                defaultScale={SpecialZoomLevel.PageFit}
                onDocumentLoad={handleOnLoad}
                renderLoader={() => <Loader size={loaderSize} />}
                {...(canZoom && {
                  plugins: [scrollModePluginInstance, zoomPluginInstance],
                })}
                {...rest}
              />
            )}
          </div>
        )}
      </Worker>

      {canZoom && (
        <div className="zoom-options">
          <ZoomOutButton />

          <Dropdown
            ref={zoomOptions}
            contentClassName="zoom-options__dropdown__content"
            className="zoom-options__dropdown"
            renderAsPortal
            trigger={
              <div className="zoom-options__dropdown__trigger">
                <CurrentScale />
                <ChevrondownIcon />
              </div>
            }
          >
            <ul>
              {customOptions.map(({ value, label }) => (
                <li key={value} onClick={() => handleOnZoom(value)}>
                  {label}
                </li>
              ))}
            </ul>
          </Dropdown>

          <ZoomInButton />
        </div>
      )}
    </>
  );
};

export default PdfViewer;
